<template>
  <div
    class="filter-stripe w-100"
    :class="{
      'event-only': eventOnly,
      'filter-stripe--empty': !hasActiveFilters,
    }"
    :style="additionalFilterStyle"
  >
    <AddFilterButton
      ref="eventFilterButton"
      class="filter-trigger filter-trigger--event lisaui:flex lisaui:shrink-0 lisaui:self-stretch"
      :filter-config="eventFilterConfig"
      :filters="eventFilters.eventFilters"
      :event-filters="eventFilters.eventFilters"
      :action-filters="actionFilters.actionFilters"
      :filter-type="eventFilterType"
      title="Ereignisse filtern"
      accent="event"
      :number-of="eventCount"
      :show-archive="showArchive"
      :types="eventTypes"
      :site-id="siteId"
      button-l10n-key="components.eventActionBoard.filter.EventActionFilter.filterEvents"
      @apply="applyEventFilters"
      @paste.stop
    />

    <div
      v-if="showQuickSearch"
      ref="quickSearchContainer"
      class="quick-search-input lisaui:flex lisaui:items-center"
    >
      <LISAInput
        v-model="quickSearchInput"
        type="search"
        :hide-label="true"
        :invalid="quickSearchInvalid"
        :placeholder="t('components.SearchList.inputSearchPlaceholder')"
        :aria-label="t('global.search')"
        @keydown.enter.prevent="commitQuickSearch"
      >
        <template #prepend>
          <LISAPopover
            placement="bottom-start"
            :aria-label="t('components.SearchList.DDHeadline').toString()"
            role="dialog"
          >
            <template #trigger="{ triggerAttrs }">
              <LISAButton
                v-bind="triggerAttrs"
                size="sm"
                text
                variant="neutral"
                icon="fas fa-search"
                :aria-label="t('components.SearchList.DDHeadline')"
              />
            </template>
            <template #default="{ close }">
              <div class="quick-search-picker">
                <div
                  class="quick-search-picker__options"
                  :class="{ 'quick-search-picker__options--single': eventOnly }"
                >
                  <section
                    v-for="group in quickSearchOptionGroups"
                    :key="group.key"
                    class="quick-search-picker__group"
                  >
                    <strong class="quick-search-picker__group-title">{{ group.label }}</strong>
                    <button
                      v-for="option in group.options"
                      :key="option.keyword"
                      class="quick-search-picker__option"
                      type="button"
                      @click="selectQuickSearchOption(option.keyword, close)"
                    >
                      <span class="quick-search-picker__option-label">
                        <i :class="option.icon" aria-hidden="true" />
                        <span>{{ option.label }}</span>
                      </span>
                      <code>{{ option.keyword }}:</code>
                    </button>
                  </section>
                </div>
              </div>
            </template>
          </LISAPopover>
        </template>
      </LISAInput>
    </div>

    <FilterInputView
      v-if="hasActiveFilters"
      class="filter-input-view"
      :event-filters="eventFilters.eventFilters"
      :action-filters="actionFilters.actionFilters"
      :event-types="eventTypes"
      :action-types="actionTypes"
      :board-id="boardId"
      @filter-selected="filterSelected"
      @filter-removed="filterRemoved"
      @all-filters-removed="allFiltersRemoved"
      @refresh-filter="refreshFilter"
    />

    <AddFilterButton
      v-if="!eventOnly"
      ref="actionFilterButton"
      class="filter-trigger filter-trigger--action lisaui:flex lisaui:shrink-0 lisaui:self-stretch"
      :filter-config="actionFilterConfig"
      :filters="actionFilters.actionFilters"
      :event-filters="eventFilters.eventFilters"
      :action-filters="actionFilters.actionFilters"
      :filter-type="actionFilterType"
      title="Aktionen filtern"
      accent="action"
      :number-of="actionCount"
      :show-archive="showArchive"
      :types="actionTypes"
      :site-id="siteId"
      button-l10n-key="components.eventActionBoard.filter.EventActionFilter.filterActions"
      @apply="applyActionFilters"
      @paste.stop
    />
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType, computed, nextTick, watch } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import AddFilterButton from "./filter/AddFilterButton.vue";
import FilterInputView from "./filter/FilterInputView.vue";
import {
  type ActionFilter,
  type EventFilter,
  Filter,
  FilterEntity,
  type ActionFilters,
  type EventFilters,
  type FilterConfig,
  type FilterEvent,
  type FilterEventActionTypes,
  type BoardFilterOptions,
} from "./filter/FilterTypes";
import { EABFilterType } from "@/graphql";
import {
  BoardFilter,
  BoardOriginFilter,
  CreatorFilter,
  DateFilter,
  DLZFilter,
  EventActionRelationFilter,
  FlagsFilter,
  IDFilter,
  OverdueFilter,
  PriorityFilter,
  RecentlyChangedFilter,
  StatusFilter,
  QuickSearchFilter,
  SiteFilter,
} from "./filter/filterImplementations";
import CustomAttributeFilter from "./filter/filterImplementations/CustomAttributeFilter";
import DescriptionDraftFilter from "./filter/filterImplementations/DescriptionDraftFilter";
import { provideBoardFilterOptions } from "./filter/useBoardFilterOptions";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";
import { LISAButton, LISAInput, LISAPopover } from "@fi-solutions/lisa-ui";
import { isKnownQuickSearchShortcut, parseQuickSearchShortcut } from "./filter/QuickSearchParser";
const props = defineProps({
  showArchive: {
    type: Boolean,
    required: false,
    default: false,
  },
  highlightArchive: {
    type: Boolean,
    default: true,
  },
  eventTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  actionTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
  eventCount: {
    type: Number,
    required: false,
    default: 0,
  },
  actionCount: {
    type: Number,
    required: false,
    default: 0,
  },
  eventOnly: {
    type: Boolean,
    required: false,
    default: false,
  },
  filterOptions: {
    type: Object as PropType<BoardFilterOptions>,
    required: false,
    default: undefined,
  },
  showQuickSearch: {
    type: Boolean,
    required: false,
    default: true,
  },
});
const eventFilters = defineModel("eventFilters", {
  type: Object as PropType<EventFilters>,
  required: true,
});
const actionFilters = defineModel("actionFilters", {
  type: Object as PropType<ActionFilters>,
  required: true,
});
const quickSearch = defineModel("quickSearch", {
  type: String,
  required: true,
});
const emit = defineEmits<{
  filterSearch: [];
  quickSearch: [];
}>();
const quickSearchInput = ref(quickSearch.value);
const quickSearchInvalid = ref(false);
const quickSearchContainer = ref<HTMLElement | null>(null);
const hasActiveFilters = computed(
  () => eventFilters.value.eventFilters.length + actionFilters.value.actionFilters.length > 0,
);
watch(quickSearch, (value) => {
  if (value !== quickSearchInput.value.trim()) {
    quickSearchInput.value = value;
  }
});
watch(quickSearchInput, () => {
  quickSearchInvalid.value = false;
});
const eventFilterType = computed(() => eventFilters.value.type ?? EABFilterType.AND);
const actionFilterType = computed(() => actionFilters.value.type ?? EABFilterType.AND);
const { showEisenhowerPriority } = useSQDCGlobalSettings();
const { t, te } = useTranslation();

const quickSearchOptionGroups = computed(() => {
  const label = (key: string) => t(key).toString();
  const event = label("global.event");
  const action = t("components.eventActionBoard.filter.EventActionFilter.filterActions", {
    count: 1,
  }).toString();
  const date = label("modules.sqdc.components.eventActionBoard.filter.labels.DateFilter");
  const deadline = label(
    "modules.sqdc.components.eventActionBoard.EventActionRevisionList.deadline",
  );
  const from = label("components.event-action.filter.ui.DateFilterUI.from");
  const until = label("components.event-action.filter.ui.DateFilterUI.until");
  const description = label(
    "modules.sqdc.components.eventActionBoard.filter.labels.DescriptionFilter",
  );

  const groups = [
    {
      key: "event",
      label: event,
      options: [
        {
          keyword: "eid",
          label: `${event} · ${label("global.id")}`,
          icon: "fas fa-hashtag",
        },
        {
          keyword: "flag",
          label: label("modules.sqdc.components.eventActionBoard.filter.labels.FlagsFilter"),
          icon: "fas fa-flag",
        },
        {
          keyword: "cre",
          label: label("modules.sqdc.components.eventActionBoard.filter.labels.CreatorFilter"),
          icon: "fas fa-user",
        },
        { keyword: "edf", label: `${event} · ${date} · ${from}`, icon: "fas fa-calendar" },
        {
          keyword: "edt",
          label: `${event} · ${date} · ${until}`,
          icon: "fas fa-calendar",
        },
        {
          keyword: "edsc",
          label: `${event} · ${description}`,
          icon: "fas fa-align-left",
        },
      ],
    },
    {
      key: "action",
      label: action,
      options: [
        {
          keyword: "aid",
          label: `${action} · ${label("global.id")}`,
          icon: "fas fa-hashtag",
        },
        {
          keyword: "resp",
          label: label("modules.sqdc.components.eventActionBoard.filter.labels.ResponsibleFilter"),
          icon: "fas fa-user-check",
        },
        {
          keyword: "dlf",
          label: `${deadline} · ${from}`,
          icon: "fas fa-calendar-check",
        },
        {
          keyword: "dlt",
          label: `${deadline} · ${until}`,
          icon: "fas fa-calendar-check",
        },
        {
          keyword: "adsc",
          label: `${action} · ${description}`,
          icon: "fas fa-align-left",
        },
        {
          keyword: "stat",
          label: label("modules.sqdc.components.eventActionBoard.filter.labels.StatusFilter"),
          icon: "fas fa-tasks",
        },
      ],
    },
  ];
  return props.eventOnly ? groups.slice(0, 1) : groups;
});

function selectQuickSearchOption(keyword: string, close: () => void) {
  quickSearchInput.value = `${keyword}:`;
  close();
  void nextTick(() =>
    quickSearchContainer.value?.querySelector<HTMLInputElement>("input")?.focus(),
  );
}

const boardFilterOptions = provideBoardFilterOptions(
  computed(() => props.boardId),
  computed(() => props.showArchive),
  computed(() => props.filterOptions),
);
function translatedFlagNames(flag: {
  name: string;
  displayNameId: number;
  abbreviationId: number;
}) {
  return [
    flag.name,
    ...(te(`dynamic.${flag.displayNameId}`) ? [t(`dynamic.${flag.displayNameId}`).toString()] : []),
    ...(te(`dynamic.${flag.abbreviationId}`)
      ? [t(`dynamic.${flag.abbreviationId}`).toString()]
      : []),
  ];
}

async function commitQuickSearch() {
  const rawInput = quickSearchInput.value;
  const input = rawInput.trim();
  if (input === "") {
    return;
  }
  await boardFilterOptions.load();
  const options = boardFilterOptions.options.value;
  const shortcut = parseQuickSearchShortcut(rawInput, {
    flags: options.flags.map((flag) => ({
      id: flag.id,
      names: translatedFlagNames(flag),
    })),
    eventUsers: options.eventUsers.map((user) => ({
      id: user.id,
      names: [user.name, user.displayName],
    })),
    actionUsers: options.actionUsers.map((user) => ({
      id: user.id,
      names: [user.name, user.displayName],
    })),
  });
  if (shortcut == null && isKnownQuickSearchShortcut(rawInput)) {
    quickSearchInvalid.value = true;
    return;
  }
  const mergeShortcutFilters = <T extends Filter>(current: T[], additions: T[]) => [
    ...additions,
    ...current.filter(
      (filter) => !additions.some((addition) => addition.constructor === filter.constructor),
    ),
  ];
  eventFilters.value = {
    updatedAt: DateTime.now(),
    eventFilters:
      shortcut == null
        ? [
            new QuickSearchFilter(input),
            ...eventFilters.value.eventFilters.filter(
              (filter) => !(filter instanceof QuickSearchFilter),
            ),
          ]
        : mergeShortcutFilters(eventFilters.value.eventFilters, shortcut.eventFilters),
    type: eventFilterType.value,
  };
  actionFilters.value = {
    updatedAt: DateTime.now(),
    actionFilters:
      shortcut == null
        ? actionFilters.value.actionFilters
        : mergeShortcutFilters(actionFilters.value.actionFilters, shortcut.actionFilters),
    type: actionFilterType.value,
  };
  quickSearchInput.value = "";
  quickSearch.value = "";
  emit("quickSearch");
}
const eventFilterConfig = computed<FilterConfig[]>(() => {
  const filters: FilterConfig[] = [
    {
      id: 0,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.IDFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: IDFilter as any,
      category: "base",
    },
    {
      id: 5,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.FlagsFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: FlagsFilter,
    },
    {
      id: 3,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.CreatorFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: CreatorFilter,
    },
    {
      id: 1,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.DateFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: DateFilter,
    },
    {
      id: 4,
      text: t(
        "modules.sqdc.components.eventActionBoard.filter.labels.DescriptionFilter",
      ).toString(),
      mode: FilterEntity.EVENT,
      filter: DescriptionDraftFilter,
    },
    {
      id: 11,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.CustomAttributesTitle"),
      mode: FilterEntity.EVENT,
      filter: CustomAttributeFilter,
    },
    {
      id: 2,
      text: t(
        "modules.sqdc.components.eventActionBoard.filter.labels.BoardOriginFilter",
      ).toString(),
      mode: FilterEntity.EVENT,
      filter: BoardOriginFilter,
      collapsed: true,
    },
    {
      id: 7,
      text: t(
        "modules.sqdc.components.eventActionBoard.filter.labels.RecentlyChangedFilter",
      ).toString(),
      mode: FilterEntity.EVENT,
      filter: RecentlyChangedFilter,
      collapsed: true,
    },
    {
      id: 8,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.DLZFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: DLZFilter,
      collapsed: true,
    },
    {
      id: 9,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.EventActionRelationFilter"),
      mode: FilterEntity.EVENT,
      filter: EventActionRelationFilter,
      collapsed: true,
    },
  ];
  if (props.boardId == null && !props.eventOnly) {
    filters.push(
      {
        id: 12,
        text: t("global.site"),
        mode: FilterEntity.EVENT,
        filter: SiteFilter,
        collapsed: true,
      },
      {
        id: 13,
        text: t("global.board"),
        mode: FilterEntity.EVENT,
        filter: BoardFilter,
        collapsed: true,
      },
    );
  }
  if (showEisenhowerPriority.value === true) {
    filters.splice(1, 0, {
      id: 10,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.priorityFilter"),
      mode: FilterEntity.EVENT,
      filter: PriorityFilter,
    });
  }
  return filters;
});
const actionFilterConfig = computed<FilterConfig[]>(() => [
  {
    id: 0,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.IDFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: IDFilter as any,
    category: "base",
  } as FilterConfig,
  {
    id: 3,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.ResponsibleFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: CreatorFilter,
  },
  {
    id: 1,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.DateFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: DateFilter,
  },
  {
    id: 4,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.DescriptionFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: DescriptionDraftFilter,
  },
  {
    id: 7,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.CustomAttributesTitle"),
    mode: FilterEntity.ACTION,
    filter: CustomAttributeFilter,
  },
  {
    id: 2,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.StatusFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: StatusFilter,
  },
  {
    id: 5,
    text: t(
      "modules.sqdc.components.eventActionBoard.filter.labels.RecentlyChangedFilter",
    ).toString(),
    mode: FilterEntity.ACTION,
    filter: RecentlyChangedFilter,
    collapsed: true,
  },
  {
    id: 6,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.OverdueFilter").toString(),
    tooltip: t("components.eventAction.filter.ui.Overdue.text").toString(),
    mode: FilterEntity.ACTION,
    filter: OverdueFilter as any,
    collapsed: true,
  },
]);
const eventFilterButton = ref<null | InstanceType<typeof AddFilterButton>>(null);
const actionFilterButton = ref<null | InstanceType<typeof AddFilterButton>>(null);
const additionalFilterStyle = computed<Record<string, string> | undefined>(() => {
  if (props.showArchive && props.highlightArchive) {
    return {
      "background-color": "var(--lisa-info)",
    };
  }
  return undefined;
});
function applyEventFilters(filters: Filter[], type: EABFilterType) {
  eventFilters.value = {
    updatedAt: DateTime.now(),
    eventFilters: filters.filter((filter): filter is EventFilter => "getEventFilter" in filter),
    type,
  };
  emit("filterSearch");
}
function applyActionFilters(filters: Filter[], type: EABFilterType) {
  actionFilters.value = {
    updatedAt: DateTime.now(),
    actionFilters: filters.filter((filter): filter is ActionFilter => "getActionFilter" in filter),
    type,
  };
  emit("filterSearch");
}
function filterRemoved({ index, type }: FilterEvent) {
  if (type === FilterEntity.EVENT) {
    const currentFilters = eventFilters.value.eventFilters.filter((_, i) => i !== index);
    eventFilters.value = {
      updatedAt: DateTime.now(),
      eventFilters: currentFilters,
      type: eventFilterType.value,
    };
  } else {
    const currentFilters = actionFilters.value.actionFilters.filter((_, i) => i !== index);
    actionFilters.value = {
      updatedAt: DateTime.now(),
      actionFilters: currentFilters,
      type: actionFilterType.value,
    };
  }
}
function filterSelected({ index, type }: FilterEvent) {
  if (type === FilterEntity.EVENT) {
    eventFilterButton.value?.loadFilter(eventFilters.value.eventFilters[index]);
  } else if (type === FilterEntity.ACTION) {
    actionFilterButton.value?.loadFilter(actionFilters.value.actionFilters[index]);
  }
}
function allFiltersRemoved() {
  eventFilters.value = {
    updatedAt: DateTime.now(),
    eventFilters: [],
    type: eventFilterType.value,
  };
  actionFilters.value = {
    updatedAt: DateTime.now(),
    actionFilters: [],
    type: actionFilterType.value,
  };
}
function refreshFilter() {
  eventFilters.value = {
    ...eventFilters.value,
    updatedAt: DateTime.now(),
  };
  actionFilters.value = {
    ...actionFilters.value,
    updatedAt: DateTime.now(),
  };
}
</script>

<style lang="scss" scoped>
.filter-stripe {
  --filter-stripe-control-height: 2.5rem;
  --filter-stripe-font-size: var(--eab-text-sm, 0.875rem);

  container-type: inline-size;
  position: sticky;
  z-index: 9;
  top: 0;
  display: grid;
  grid-template-areas: "event search filters action";
  grid-template-columns: max-content auto minmax(0, 1fr) max-content;
  min-height: 3rem;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem 0 0.3rem;
  padding: 0.4rem;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-color);
}

.filter-stripe.event-only {
  grid-template-areas: "event search filters";
  grid-template-columns: max-content auto minmax(0, 1fr);
}

.filter-stripe--empty {
  grid-template-areas: "event search action";
  grid-template-columns: max-content minmax(8rem, 1fr) max-content;
}

.filter-stripe--empty.event-only {
  grid-template-areas: "event search";
  grid-template-columns: max-content minmax(8rem, 1fr);
}

.filter-stripe--empty .quick-search-input {
  width: 100%;
}

.filter-input-view {
  grid-area: filters;
  height: var(--filter-stripe-control-height);
  min-width: 0;
  min-height: var(--filter-stripe-control-height);
  flex: 1;
  align-self: center;
  border: 0 !important;
  border-radius: 0 !important;

  :deep(.empty-filter-message) {
    font-size: var(--filter-stripe-font-size);
  }
}

.quick-search-input {
  grid-area: search;
  width: clamp(12rem, 20cqw, 22rem);
  height: var(--filter-stripe-control-height);
  min-width: 10rem;
  min-height: var(--filter-stripe-control-height);
  align-self: center;

  :deep(.lisa-field) {
    width: 100%;
    height: 100%;
  }

  :deep(.lisa-input__control) {
    font-size: var(--filter-stripe-font-size);
  }
}

.quick-search-picker {
  width: min(56rem, calc(100vw - 2rem));
  padding: 0.75rem;
}

.quick-search-picker__title {
  display: block;
  margin: 0 0.35rem 0.15rem;
}

.quick-search-picker__description {
  margin: 0 0.35rem 0.65rem;
  color: var(--p-text-muted-color);
  font-size: var(--eab-text-xs, 0.75rem);
}

.quick-search-picker__options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.quick-search-picker__options--single {
  grid-template-columns: minmax(0, 1fr);
}

.quick-search-picker__group {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 0.2rem;
}

.quick-search-picker__group-title {
  padding: 0.15rem 0.5rem 0.35rem;
  color: var(--p-text-muted-color);
  font-size: var(--eab-text-xs, 0.75rem);
}

.quick-search-picker__option {
  display: grid;
  width: 100%;
  min-width: 0;
  min-height: 2.25rem;
  grid-template-columns: minmax(0, 1fr) 10.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.35rem 0.5rem;
  border: 0;
  border-radius: var(--p-border-radius-sm);
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;

  &:hover {
    background: var(--p-surface-100);
  }

  &:focus-visible {
    outline: 2px solid var(--p-primary-color);
    outline-offset: 1px;
  }

  code {
    justify-self: start;
    color: var(--p-text-muted-color);
    font-size: var(--eab-text-xs, 0.75rem);
    text-align: left;
    white-space: nowrap;
  }
}

.quick-search-picker__option-label {
  display: grid;
  min-width: 0;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  align-items: center;
  gap: 0.65rem;
  text-align: left;
  white-space: nowrap;

  > i {
    width: 1.25rem;
    text-align: left;
  }
}

.filter-trigger {
  width: max-content;
  height: var(--filter-stripe-control-height);
  min-height: var(--filter-stripe-control-height);
  flex: 0 0 auto;
  align-self: center;

  :deep(.lisa-button) {
    width: max-content;
    max-width: none;
    height: 100%;
  }

  :deep(.filter-button-label) {
    font-size: var(--filter-stripe-font-size);
  }
}

.filter-trigger--event {
  grid-area: event;
}

.filter-trigger--action {
  grid-area: action;
}

@media (max-width: 80rem) {
  .filter-stripe:not(.filter-stripe--empty) {
    grid-template-areas:
      "event search action"
      "filters filters filters";
    grid-template-columns: max-content minmax(8rem, 1fr) max-content;
  }

  .filter-stripe.event-only:not(.filter-stripe--empty) {
    grid-template-areas:
      "event search"
      "filters filters";
    grid-template-columns: max-content minmax(8rem, 1fr);
  }

  .filter-stripe:not(.filter-stripe--empty) .quick-search-input {
    width: 100%;
  }
}

@media (max-width: 48rem) {
  .filter-stripe {
    padding: 0.25rem;
  }

  .filter-input-view {
    overflow: hidden;
  }

  .quick-search-input {
    width: 100%;
  }

  .quick-search-picker__options {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 42rem) {
  .quick-search-input {
    width: 100%;
    min-width: 0;
  }

  .filter-trigger {
    width: max-content;
    min-width: 0;
  }

  .filter-stripe:not(.filter-stripe--empty) .filter-input-view {
    min-height: 2.5rem;
  }
}

@media (max-width: 30rem) {
  .filter-stripe {
    grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
  }

  .filter-stripe.event-only {
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .filter-trigger {
    width: 2.75rem;
    min-width: 2.75rem;
    flex-basis: 2.75rem;

    :deep(.lisa-button) {
      width: 100%;
      max-width: 100%;
    }
  }
}
</style>
